import React from 'react';

import { Alert, Avatar, Box, Grid, Stack, Typography } from '@mui/material';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { venueUnblockUserApi } from '../../../../api/users/venueUnblockUserApi';
import { VenueBlockUserInput } from '../../../../graphql/API';
import useListBlockedUserQuery from '../../../../hooks/reactQuery/useListBlockedUserQuery';
import useVenueId from '../../../../hooks/useVenueId';
import InfiniteScroll from '../../../InfiniteScroll/InfiniteScroll';
import { getSuccessMessage } from '../../ListAccessRequest/components/helper';
import { TabOption } from '../../ListAccessRequest/components/types';
import { BlockedUsersSkeleton } from './BlockedUsersSkeleton';
import { EmptyContent } from './EmptyContent';
import LoadingButton from '../../../Button/LoadingButton';

export const BlockedUsersList = () => {
  const venueId = useVenueId();
  const queryClient = useQueryClient();

  const [dictSuccessText, setSuccessText] = React.useState<Record<string, React.ReactNode | null>>(
    {},
  );

  const { data, isFetching, isLoading, fetchNextPage, hasNextPage } =
    useListBlockedUserQuery(undefined);

  const mutation = useMutation({
    mutationFn: venueUnblockUserApi,
    onSuccess: async () => {
      const queryKey = 'list-blocked-users';
      await queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const handleAction = React.useCallback(
    async (input: VenueBlockUserInput) => {
      if (!input) {
        return;
      }
      await mutation.mutateAsync(input);
      setSuccessText({ blocked: getSuccessMessage(TabOption.blocked, data, input) });
    },
    [data, mutation],
  );

  const renderContent = () => {
    if (isLoading || data.length > 0) {
      return (
        <InfiniteScroll
          isLoading={isLoading}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
        >
          {isLoading ? (
            <BlockedUsersSkeleton />
          ) : (
            <Stack spacing={3} sx={{ flex: '1' }}>
              {data.map((user) => (
                <Grid container alignItems={'center'}>
                  <Grid item xs={5}>
                    <Box display={'flex'} alignItems={'center'} gap={'24px'}>
                      <Avatar
                        sx={{ height: '48px', width: '48px' }}
                        variant='circular'
                        alt={String(user.picture?.thumbnail)}
                        src={String(user.picture?.thumbnail)}
                      />
                      <Typography fontSize={'15px'}>
                        {user.firstname} {user.lastname}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography fontSize={'15px'}>
                      Block {dayjs.utc(user.blockedDate).format('LL')}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Box textAlign={'end'}>
                      <LoadingButton
                        loading={mutation.isPending}
                        variant='outlined'
                        onClick={() => {
                          handleAction({ venueID: Number(venueId), userID: Number(user.id) });
                        }}
                      >
                        Unblock
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Stack>
          )}
        </InfiniteScroll>
      );
    }

    return <EmptyContent />;
  };

  const successText = React.useMemo(() => dictSuccessText['blocked'], [dictSuccessText]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: 2,
          px: 4,
          flex: '1',
          overflowY: 'auto',
        }}
      >
        {renderContent()}
        {mutation.isSuccess && <Alert severity='success'>{successText}</Alert>}
      </Box>
    </Box>
  );
};
